import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';

/**
 * UnifiedSettingsDialog - Centralized settings dialog for the entire application
 * Combines History and Camera (Face Match) settings in one place
 */
export default function UnifiedSettingsDialog({
  open,
  onClose,
  // History settings
  deleteConfirmation,
  onDeleteConfirmationChange,
  autoEnableVoice,
  onAutoEnableVoiceChange,
  stealthMode,
  onStealthModeChange,
  // Camera settings
  removeBg,
  setRemoveBg,
  sliderValue,
  setSliderValue,
  maxFacesValue,
  setMaxFacesValue,
  showBoundingBoxes,
  setShowBoundingBoxes,
  immediateFaceMatch,
  setImmediateFaceMatch,
  updateCharacteristicsOnMatch,
  setUpdateCharacteristicsOnMatch,
  compressionQuality,
  setCompressionQuality,
  autoAdjustCompression,
  setAutoAdjustCompression,
  autoCreateEventOnAIMatch,
  setAutoCreateEventOnAIMatch,
  // Location settings
  locationHighAccuracy,
  setLocationHighAccuracy,
  locationAcquisitionTimeout,
  setLocationAcquisitionTimeout,
  locationCacheMaxAge,
  setLocationCacheMaxAge,
  // Aggregator settings
  aggregatorEventLimit,
  setAggregatorEventLimit,
  // Video settings
  videoEnabled,
  setVideoEnabled,
  videoFrameInterval,
  setVideoFrameInterval
}) {
  const handleResetToDefaults = () => {
    // Reset history settings
    onDeleteConfirmationChange('match', false);
    onDeleteConfirmationChange('assignment', true);
    onAutoEnableVoiceChange(false);
    onStealthModeChange(false);

    // Reset camera settings
    setRemoveBg(false);
    setSliderValue(50);
    setMaxFacesValue(1);
    setShowBoundingBoxes(true);
    setImmediateFaceMatch(false);
    setUpdateCharacteristicsOnMatch(true);
    setCompressionQuality(0.8);
    setAutoAdjustCompression(false);
    setAutoCreateEventOnAIMatch(true);

    // Reset location settings
    setLocationHighAccuracy(true);
    setLocationAcquisitionTimeout(10000);
    setLocationCacheMaxAge(60000);

    // Reset aggregator settings
    setAggregatorEventLimit(500);

    // Reset video settings
    setVideoEnabled(false);
    setVideoFrameInterval(3);
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Settings</DialogTitle>
      <DialogContent sx={{ py: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          {/* Left Column */}
          <Box>
            {/* Camera/Face Match Settings Section */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
                Face Match Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={showBoundingBoxes}
                      onChange={(e) => setShowBoundingBoxes(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Bounding Boxes</Typography>}
                />
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={immediateFaceMatch}
                      onChange={(e) => setImmediateFaceMatch(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Immediate Face Match</Typography>}
                />
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={autoCreateEventOnAIMatch}
                      onChange={(e) => setAutoCreateEventOnAIMatch(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Auto-Create Event on AI Match</Typography>}
                />
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={updateCharacteristicsOnMatch}
                      onChange={(e) => setUpdateCharacteristicsOnMatch(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Update Characteristics on Match</Typography>}
                />
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Match Threshold: {sliderValue}
                  </Typography>
                  <Slider
                    value={sliderValue}
                    onChange={(e, newValue) => setSliderValue(newValue)}
                    min={50}
                    max={90}
                    step={10}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{ mt: 0, mb: 0.5 }}
                  />
                </Box>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Max Faces: {maxFacesValue}
                  </Typography>
                  <Slider
                    value={maxFacesValue}
                    onChange={(e, newValue) => setMaxFacesValue(newValue)}
                    min={1}
                    max={20}
                    step={1}
                    valueLabelDisplay="auto"
                    size="small"
                    sx={{ mt: 0, mb: 0.5 }}
                  />
                </Box>
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={autoAdjustCompression}
                      onChange={(e) => setAutoAdjustCompression(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Auto-Adjust Compression</Typography>}
                />
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Compression Quality: {(compressionQuality * 100).toFixed(0)}%
                    {autoAdjustCompression && " (auto)"}
                  </Typography>
                  <Slider
                    value={compressionQuality}
                    onChange={(e, newValue) => setCompressionQuality(newValue)}
                    disabled={autoAdjustCompression}
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
                    size="small"
                    sx={{ mt: 0, mb: 0.5 }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Aggregator Settings Section */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
                Aggregator Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <TextField
                  label="Event Limit"
                  type="number"
                  size="small"
                  value={aggregatorEventLimit}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value > 0) setAggregatorEventLimit(value);
                  }}
                  inputProps={{ min: 100, max: 2000, step: 100 }}
                  helperText="Maximum events in memory (100-2000)"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>

          </Box>

          {/* Right Column */}
          <Box>
            {/* Stealth Settings Section */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
                Stealth Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={stealthMode}
                      onChange={(e) => onStealthModeChange(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Stealth Mode</Typography>}
                />
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={videoEnabled}
                      onChange={(e) => setVideoEnabled(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Stealth Video Capture</Typography>}
                />
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Frame Interval: {videoFrameInterval}s
                  </Typography>
                  <Slider
                    value={videoFrameInterval}
                    onChange={(e, newValue) => setVideoFrameInterval(newValue)}
                    disabled={!videoEnabled}
                    min={3}
                    max={60}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}s`}
                    size="small"
                    sx={{ mt: 0, mb: 0.5 }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Location Settings Section */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
                Location Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={locationHighAccuracy}
                      onChange={(e) => setLocationHighAccuracy(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">High Accuracy (GPS)</Typography>}
                />
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Acquisition Timeout: {(locationAcquisitionTimeout / 1000).toFixed(1)}s
                  </Typography>
                  <Slider
                    size="small"
                    value={locationAcquisitionTimeout}
                    onChange={(e, value) => setLocationAcquisitionTimeout(value)}
                    min={5000}
                    max={30000}
                    step={1000}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${(value / 1000).toFixed(1)}s`}
                  />
                </Box>
                <Box sx={{ px: 2, py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    Cache Max Age: {locationCacheMaxAge === 0 ? 'No cache' : `${(locationCacheMaxAge / 60000).toFixed(1)}m`}
                  </Typography>
                  <Slider
                    size="small"
                    value={locationCacheMaxAge}
                    onChange={(e, value) => setLocationCacheMaxAge(value)}
                    min={0}
                    max={300000}
                    step={30000}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => value === 0 ? 'No cache' : `${(value / 60000).toFixed(1)}m`}
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Operator Settings Section */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.95rem' }}>
                Operator Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.25, fontSize: '0.7rem', fontWeight: 500 }}>
                  Delete Confirmation
                </Typography>
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={deleteConfirmation.match}
                      onChange={(e) => onDeleteConfirmationChange('match', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Match events</Typography>}
                />
                <FormControlLabel
                  sx={{ my: 0, minHeight: 36 }}
                  control={
                    <Switch
                      size="small"
                      checked={deleteConfirmation.assignment}
                      onChange={(e) => onDeleteConfirmationChange('assignment', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Assignment events</Typography>}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetToDefaults} color="secondary">
          Reset to Defaults
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
